/**
 * /assignments — single-page Class → Assignments → Score-entry surface.
 *
 *   1. Class selector at top (defaults to first class returned).
 *   2. "+ New Assignment" button reveals an inline create form.
 *   3. List of existing assignments for the selected class.
 *   4. Click an assignment row → ScoreEntryGrid expands inline.
 *      Saving scores triggers the backend profiler so the affected
 *      students' radar charts update on next visit.
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FolderPanel } from "../workbench/FolderPanel";
import { useMyEmployees } from "../workbench/EmployeeSwitcher";
import { HomeworkGeneratorPanel } from "./HomeworkGeneratorPanel";

import {
  authHeaders,
  useAssignments,
  useClasses,
  useClassWithRoster,
  useCreateAssignment,
  useUpdateAssignment,
  type Assignment,
  useSubmitScores,
  useAssignmentDetail,
  type AssignmentType,
} from "../../api/school";
import { ScoreEntryGrid } from "../../components/ScoreEntryGrid";
import { ScoreImageUpload } from "../../components/ScoreImageUpload";

const SKILL_TAGS: readonly string[] = [
  "listening",
  "speaking",
  "reading",
  "writing",
  "vocabulary",
  "grammar",
];

/** 考察方向选项跟老师学科走(Jarod 2026-08-12)。英语沿用六技能轴
 *  (i18n label + 雷达轴映射);其他学科调 /ai/suggest-tags 由 AI 按
 *  学科+本班已讲单元生成,失败回落学科通用方向。 */
function useSubjectTags(classId: string, pickedSlug?: string) {
  const { employees } = useMyEmployees();
  // 学科标签挂钩(Jarod 2026-08-14):多学科老师显式选;单学科自动。
  // 与页面 effectiveSlug 同款:多学科也默认第一个,不留"无学科"分支
  const slug = pickedSlug || employees[0]?.slug || null;
  const isEnglish = !slug || slug === "english";
  const q = useQuery({
    queryKey: ["ai", "suggest-tags", classId, slug],
    queryFn: async () => {
      const r = await fetch("/api/school/ai/suggest-tags", {
        method: "POST",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ class_id: classId, locale: "zh-CN", subject_slug: slug }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return (await r.json()) as { tags: string[] };
    },
    enabled: !isEnglish,
    staleTime: 10 * 60_000,
  });
  return { isEnglish, aiTags: q.data?.tags ?? [], loading: q.isLoading && !isEnglish };
}

const ASSIGNMENT_TYPES: AssignmentType[] = [
  "homework",
  "in_class",
  "project",
  "presentation",
];

export function AssignmentPage() {
  const { t } = useTranslation();
  const classesQuery = useClasses();
  const [selectedClassId, setSelectedClassId] = useState<string>("");
  const [expandedAssignmentId, setExpandedAssignmentId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const effectiveClassId = selectedClassId || classesQuery.data?.[0]?.id || "";

  const classDetail = useClassWithRoster(effectiveClassId);
  const assignments = useAssignments(effectiveClassId);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <header className="mb-6 flex items-baseline justify-between">
        <h1 className="text-2xl font-semibold text-slate-800">{t("assignments.title")}</h1>
        {classesQuery.data && classesQuery.data.length === 0 && (
          <span className="text-xs text-amber-600">{t("common.noClassAssigned")}</span>
        )}
        <select
          value={effectiveClassId}
          onChange={(e) => {
            setSelectedClassId(e.target.value);
            setExpandedAssignmentId(null);
            setShowCreate(false);
            setEditingId(null);
          }}
          className="px-3 py-1.5 rounded border border-slate-200 text-sm"
        >
          {classesQuery.data?.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </header>

      {classDetail.data && (
        <p className="text-sm text-slate-500 mb-4">
          {t("assignments.classStudentCount", { count: classDetail.data.student_count })}
        </p>
      )}

      <div className="mb-4 flex justify-end">
        <button
          type="button"
          onClick={() => setShowCreate((v) => !v)}
          className="px-3 py-1.5 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600"
        >
          {showCreate ? t("assignments.cancel") : t("assignments.newAssignment")}
        </button>
      </div>

      {showCreate && classDetail.data && classDetail.data.teacher_id && (
        <AssignmentForm
          classId={effectiveClassId}
          teacherId={classDetail.data.teacher_id}
          onDone={() => setShowCreate(false)}
        />
      )}
      {showCreate && (!classDetail.data?.teacher_id) && (
        <p className="mb-4 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
          {t("assignments.noTeacherWarning")}
        </p>
      )}

      {assignments.isLoading && <p className="text-slate-500 italic">{t("common.loading")}</p>}
      {assignments.error && (
        <p className="text-sm text-red-600">{(assignments.error as Error).message}</p>
      )}

      {!assignments.isLoading && (assignments.data?.length ?? 0) === 0 && !showCreate && (
        <div className="rounded border border-slate-200 bg-white p-8 text-center text-slate-500">
          {t("assignments.empty")}
        </div>
      )}

      <ul className="space-y-3">
        {assignments.data?.map((a) => (
          <li key={a.id} className="rounded-lg border border-slate-200 bg-white">
            <button
              type="button"
              onClick={() =>
                setExpandedAssignmentId((id) => (id === a.id ? null : a.id))
              }
              className="w-full text-left p-4 hover:bg-slate-50 flex items-center justify-between"
            >
              <div>
                <div className="text-base font-medium text-slate-800">{a.title}</div>
                <div className="text-xs text-slate-500 mt-1 flex gap-3 flex-wrap">
                  <span>{t(`assignments.types.${a.type}`)}</span>
                  <span>{t("assignments.row.assignedDate", { date: a.assigned_date })}</span>
                  <span>{t("assignments.row.maxScore", { max: a.max_score })}</span>
                  {a.knowledge_tags.length > 0 && (
                    <span>
                      {t("assignments.row.knowledgeTags", {
                        tags: a.knowledge_tags
                          .map((tg) => t(`common.skills.${tg}`, { defaultValue: tg }))
                          .join(" / "),
                      })}
                    </span>
                  )}
                </div>
              </div>
              <span className="text-xs text-sky-600">
                {expandedAssignmentId === a.id
                  ? t("assignments.row.collapse")
                  : t("assignments.row.enterScores")}
              </span>
            </button>

            {expandedAssignmentId === a.id && classDetail.data && (
              <div className="border-t border-slate-100 p-4 bg-slate-50 space-y-4">
                {/* 编辑作业本身(标题/类型/日期/满分/标签)—— 2026-09-14 之前
                    展开只有录分,建错了只能重建。 */}
                {editingId === a.id ? (
                  <AssignmentForm
                    classId={effectiveClassId}
                    teacherId={classDetail.data.teacher_id ?? a.teacher_id}
                    initial={a}
                    onDone={() => setEditingId(null)}
                  />
                ) : (
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => setEditingId(a.id)}
                      className="px-3 py-1 rounded border border-slate-300 bg-white text-xs text-slate-700 hover:bg-slate-100"
                    >
                      {t("assignments.edit.button")}
                    </button>
                  </div>
                )}
                {/* 作业单两条路,老师自选:AI 直接生成,或手工上传。
                    两者产物落进同一个 assignments/{id} 文件夹。 */}
                <HomeworkGeneratorPanel
                  assignmentId={a.id}
                  assignmentTitle={a.title}
                  className={classDetail.data.name}
                  assignedDate={a.assigned_date}
                  classId={effectiveClassId}
                />
                {/* 作业留档:上传作业单/答案文档 → 可入库(不必在聊天里生成产物) */}
                <div className="rounded-lg border border-slate-200 bg-white p-3">
                  <FolderPanel
                    subdir={`assignments/${a.id}`}
                    heading={t("assignments.filing")}
                    promoteEnabled
                  />
                </div>
                <AssignmentScoreEditor
                  assignmentId={a.id}
                  maxScore={Number(a.max_score)}
                  students={classDetail.data.students}
                />
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Create / edit form ─────────────────────────────────────────────────────

/** One form for both: with `initial` it edits that assignment (PATCH),
 *  without it creates a new one. Same fields either way. */
function AssignmentForm({
  classId,
  teacherId,
  initial,
  onDone,
}: {
  classId: string;
  teacherId: string;
  initial?: Assignment;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const today = new Date().toISOString().slice(0, 10);
  const [title, setTitle] = useState(initial?.title ?? "");
  const [type, setType] = useState<AssignmentType>(initial?.type ?? "homework");
  const [assignedDate, setAssignedDate] = useState(initial?.assigned_date ?? today);
  const [maxScore, setMaxScore] = useState(initial ? String(Number(initial.max_score)) : "100");
  const [tags, setTags] = useState<Set<string>>(new Set(initial?.knowledge_tags ?? []));

  const create = useCreateAssignment();
  const update = useUpdateAssignment(initial?.id ?? "", classId);
  const isEdit = Boolean(initial);
  const pending = isEdit ? update.isPending : create.isPending;
  const mutError = isEdit ? update.error : create.error;
  const { employees: myEmployees } = useMyEmployees();
  const [pickedSubject, setPickedSubject] = useState<string>("");
  // 多学科默认第一个学科(与下拉显示一致),绝不回落英语六轴。
  const effectiveSlug = pickedSubject || (myEmployees[0]?.slug ?? "");
  const subjectTags = useSubjectTags(classId, effectiveSlug || undefined);

  function toggleTag(tag: string) {
    setTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  async function submit() {
    if (!title.trim()) return;
    if (isEdit) {
      await update.mutateAsync({
        title: title.trim(),
        type,
        assigned_date: assignedDate,
        max_score: Number(maxScore),
        knowledge_tags: [...tags],
      });
      onDone();
      return;
    }
    await create.mutateAsync({
      class_id: classId,
      teacher_id: teacherId,
      title: title.trim(),
      type,
      assigned_date: assignedDate,
      max_score: Number(maxScore),
      knowledge_tags: [...tags],
    });
    setTitle("");
    setTags(new Set());
    onDone();
  }

  return (
    <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3">
        {isEdit ? t("assignments.edit.header") : t("assignments.create.header")}
      </h3>
      <div className="grid grid-cols-1 gap-3 mb-3 sm:grid-cols-2">
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("assignments.create.titleLabel")}</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={t("assignments.create.titlePlaceholder")}
            className="w-full px-2 py-1 rounded border border-slate-200"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("assignments.create.typeLabel")}</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as AssignmentType)}
            className="w-full px-2 py-1 rounded border border-slate-200"
          >
            {ASSIGNMENT_TYPES.map((typ) => (
              <option key={typ} value={typ}>
                {t(`assignments.types.${typ}`)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("assignments.create.assignedLabel")}</span>
          <input
            type="date"
            value={assignedDate}
            onChange={(e) => setAssignedDate(e.target.value)}
            className="w-full px-2 py-1 rounded border border-slate-200"
          />
        </label>
        <label className="text-sm">
          <span className="block text-xs text-slate-600 mb-1">{t("assignments.create.maxScoreLabel")}</span>
          <input
            type="number"
            min={1}
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
            className="w-full px-2 py-1 rounded border border-slate-200"
          />
        </label>
      </div>
      {myEmployees.length > 1 && (
        <label className="mb-2 flex items-center gap-2 text-sm">
          <span className="text-xs text-slate-600">{t("exams.create.subjectLabel")}</span>
          <select
            value={effectiveSlug}
            onChange={(e) => setPickedSubject(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1 text-sm"
          >
            {myEmployees.map((emp: { slug: string; name: string }) => (
              <option key={emp.slug} value={emp.slug}>{emp.name}</option>
            ))}
          </select>
        </label>
      )}
      <div className="mb-3">
        <div className="text-xs text-slate-600 mb-1">
          {t("assignments.create.tagsHint")}
        </div>
        {subjectTags.loading && (
          <p className="text-xs text-slate-400 italic mb-1">{t("assignments.create.tagsLoading")}</p>
        )}
        <div className="flex flex-wrap gap-2">
          {(subjectTags.isEnglish ? SKILL_TAGS : subjectTags.aiTags).map((tag: string) => (
            <label
              key={tag}
              className={`text-xs px-2 py-1 rounded border cursor-pointer ${
                tags.has(tag)
                  ? "bg-sky-500 text-white border-sky-500"
                  : "bg-white text-slate-600 border-slate-200"
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={tags.has(tag)}
                onChange={() => toggleTag(tag)}
              />
              {subjectTags.isEnglish ? t(`common.skills.${tag}`) : tag}
            </label>
          ))}
        </div>
      </div>
      <div className="flex items-center gap-3 justify-end">
        {mutError && (
          <span className="text-xs text-red-600">{(mutError as Error).message}</span>
        )}
        {isEdit && (
          <button
            type="button"
            onClick={onDone}
            className="px-3 py-1.5 rounded border border-slate-200 bg-white text-slate-600 text-sm hover:bg-slate-50"
          >
            {t("common.cancel")}
          </button>
        )}
        <button
          type="button"
          onClick={submit}
          disabled={!title.trim() || pending}
          className="px-3 py-1.5 rounded bg-sky-500 text-white text-sm hover:bg-sky-600 disabled:opacity-50"
        >
          {isEdit
            ? pending ? t("common.saving") : t("common.save")
            : pending ? t("assignments.create.submitting") : t("assignments.create.submit")}
        </button>
      </div>
    </div>
  );
}

// ── Score editor — two modes: manual grid OR photo upload + AI extraction ──

function AssignmentScoreEditor({
  assignmentId,
  maxScore,
  students,
}: {
  assignmentId: string;
  maxScore: number;
  students: import("../../api/school").Student[];
}) {
  const { t } = useTranslation();
  const submit = useSubmitScores(assignmentId);
  const [mode, setMode] = useState<"manual" | "image">("manual");
  // 回填已录入的分数(2026-09-19):此前展开作业永远是空表,老师看不到
  // 已经录过的成绩,也没法改。
  const detail = useAssignmentDetail(assignmentId);
  const initialScores = useMemo(() => {
    const m: Record<string, number | null> = {};
    for (const sc of detail.data?.scores ?? []) m[sc.student_id] = sc.score == null ? null : Number(sc.score);
    return m;
  }, [detail.data]);

  return (
    <div>
      <div className="mb-3 inline-flex rounded border border-slate-200 overflow-hidden text-sm">
        {(["manual", "image"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={`px-3 py-1 ${
              mode === m
                ? "bg-sky-500 text-white"
                : "bg-white text-slate-600 hover:bg-slate-50"
            }`}
          >
            {t(`assignments.editor.${m}`)}
          </button>
        ))}
      </div>

      {mode === "manual" && (
        <ScoreEntryGrid
          students={students}
          maxScore={maxScore}
          initialScores={initialScores}
          onSave={async (rows) => {
            await submit.mutateAsync(
              rows.map((r) => ({ student_id: r.student_id, score: r.score })),
            );
          }}
        />
      )}

      {mode === "image" && (
        <ScoreImageUpload
          assignmentId={assignmentId}
          students={students}
          maxScore={maxScore}
        />
      )}
    </div>
  );
}
